import { useRef, useState } from 'react';

import Button from 'components/Button';
import IconButton from 'components/IconButton';
import { LayoutContainer } from 'components/Layout';
import Link from 'components/Link';
import type { SitecoreLink } from 'models/sitecore';
import { cn } from 'utils/classNames';
import { filterTruthy } from 'utils/collection';
import { afterNextPaint } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	/** Custom aria-label for the nav-tag. */
	ariaLabel?: string;
	className?: string;
	links: SitecoreLink[] | undefined;
}

export default function Breadcrumbs({
	ariaLabel,
	className,
	links: linksProp,
}: Props) {
	const { t } = useI18n();
	const [isExpanded, setIsExpanded] = useState(false);
	const linksRef = useRef<(HTMLAnchorElement | null)[]>([]);

	const links = filterTruthy(linksProp, 'href', 'text');
	const linkCount = links.length;

	if (linkCount < 2) {
		return null;
	}

	// Small screens:
	// - For exactly two links, show the previous level as a back arrow link.
	// - For more than two links, show an ellipsis button plus the previous
	//   level. Pressing the button expands to full breadcrumbs.
	const prevLevelIndex = linkCount - 2;
	const backLink = linkCount === 2 ? links[prevLevelIndex] : undefined;
	const hasExpand = linkCount > 2;
	const isCollapsed = hasExpand && !isExpanded;

	const handleExpandClick = () => {
		setIsExpanded(true);
		afterNextPaint(() => {
			linksRef.current[prevLevelIndex - 1]?.focus();
		});
	};

	const listItemClassName = 'flex items-center';
	const dividerClassName =
		'after:mx-2 after:h-3.5 after:rotate-12 after:border-r after:border-r-current after:pointer-events-none';

	return (
		<LayoutContainer outerClassName={className}>
			<nav aria-label={ariaLabel || t('breadcrumbs_label')}>
				{backLink && (
					<IconButton
						icon="arrow-back"
						text={backLink.text}
						href={backLink.href}
						textDisplay="trailing"
						size="xs"
						className="align-top md:hidden"
					/>
				)}
				<ol
					className={cn('flex flex-wrap gap-y-3', backLink && 'max-md:hidden')}
				>
					{isCollapsed && (
						<li
							className={cn('md:hidden', listItemClassName, dividerClassName)}
						>
							<Button
								onClick={handleExpandClick}
								aria-label={t('breadcrumbs_expand_button_label')}
								variant="text-inherit"
								className="relative after:absolute after:-inset-x-3 after:-inset-y-2"
							>
								&hellip;
							</Button>
						</li>
					)}
					{links.map((link, i) => {
						const isLast = linkCount - 1 === i;

						return (
							<li
								key={link.href}
								className={cn(
									listItemClassName,
									!isLast && dividerClassName,
									isCollapsed && i !== prevLevelIndex && 'max-md:hidden',
									isCollapsed && i === prevLevelIndex && 'max-md:after:hidden',
								)}
							>
								<Link
									ref={(el) => {
										linksRef.current[i] = el;
									}}
									href={link.href}
									aria-current={isLast ? 'page' : undefined}
									className={cn(
										'inline-block',
										isLast && 'hover:underline-strong',
									)}
									underline={!isLast}
									hasExpandedHitArea
								>
									{link.text}
								</Link>
							</li>
						);
					})}
				</ol>
			</nav>
		</LayoutContainer>
	);
}
Breadcrumbs.displayName = 'Breadcrumbs';
